import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ArrowRight, BookOpen, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const HelpHub = forwardRef<HTMLElement>((_, ref) => {
  const { data: articles = [] } = useQuery({
    queryKey: ["help-hub-latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("knowledge_articles")
        .select("id, slug, title, category")
        .order("updated_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data;
    },
  });

  if (articles.length === 0) return null;

  return (
    <section ref={ref} className="border-t border-border bg-secondary/20 py-16 lg:py-20">
      <div className="container">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5 text-primary" />
              Wissen & Hilfe
            </div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-foreground lg:text-4xl">
              Direkt loslegen mit NextInvoice
            </h2>
            <p className="mt-3 max-w-xl text-muted-foreground">
              Anleitungen, Tipps und Antworten – alles, was Sie für den schnellen Start und den täglichen Einsatz brauchen.
            </p>
          </div>
          <Button asChild variant="outline" className="rounded-xl">
            <Link to="/hilfe">
              Alle Artikel
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          {articles.map((a) => (
            <Link
              key={a.id}
              to={`/hilfe/${a.slug}`}
              className="group flex flex-col justify-between rounded-2xl border border-border bg-card p-6 transition-all hover:border-primary/30 hover:shadow-md"
            >
              <div>
                <span className="inline-block rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-medium text-primary">
                  {a.category}
                </span>
                <h3 className="mt-3 font-display text-lg font-semibold text-foreground transition-colors group-hover:text-primary">
                  {a.title}
                </h3>
              </div>
              <div className="mt-6 flex items-center text-sm font-medium text-primary">
                Artikel lesen
                <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
});
HelpHub.displayName = "HelpHub";

export default HelpHub;
